import { Box, Stack, Typography, Button, Alert } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import axiosInstance from 'store/axiosInstance'
import { ILot } from 'types/lots'
import { EPaymentPayerStatus, TPaymentPayerStatus } from 'types/payments'
import { v4 as uuidv4 } from 'uuid'

type Props = {
  participationFee?: number
  lot: ILot
  paymentPayerStatus?: TPaymentPayerStatus
  isSubmissionTimePassed: boolean
}

export const ParticipationFee = ({
  participationFee,
  lot,
  paymentPayerStatus,
  isSubmissionTimePassed,
}: Props) => {
  const { t } = useTranslation()

  const params = useMemo(() => {
    return {
      paymentLotId: lot.id,
      browserScreenHeight: window.screen.height,
      browserScreenWidth: window.screen.width,
      browserTimeZone: new Date().getTimezoneOffset() * -1,
      id: uuidv4(),
    }
  }, [lot])

  const handlePayment = async () => {
    try {
      await axiosInstance
        .get('/payments', { params, responseType: 'arraybuffer' })
        .then((response: any) => {
          const decoder = new TextDecoder('utf-8')
          const htmlString = decoder.decode(response)
          const blob = new Blob([htmlString], { type: 'text/html' })
          const url = URL.createObjectURL(blob)
          const newWindow = window.open(url, '_blank')
          if (newWindow) {
            newWindow.onload = () => {
              const form = newWindow.document.getElementById(
                'payment'
              ) as HTMLFormElement
              if (form) {
                form.submit()
              } else {
                console.error('Form not found in the new window')
              }
            }
          } else {
            console.error('Failed to open new window')
          }
        })
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Stack boxShadow={1} sx={{ width: '100%', background: '#FFF' }}>
      <Stack sx={{ p: 1.5, backgroundColor: 'primary.main', color: '#FFF' }}>
        <Typography variant="caption" fontWeight="bold">
          {t('participation-fee')}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 3 }}
      >
        <Box sx={{ border: '1px solid #ccc', padding: 1, borderRadius: '6px' }}>
          {participationFee ? `${participationFee} AZN` : t('notSpecified')}
        </Box>
        {paymentPayerStatus?.status !== EPaymentPayerStatus.AUTHORIZED &&
          !isSubmissionTimePassed && (
            <Button
              onClick={handlePayment}
              variant="contained"
              style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            >
              {t('pay')}
            </Button>
          )}
      </Stack>
      {paymentPayerStatus?.status === EPaymentPayerStatus.AUTHORIZED ? (
        <Box sx={{ p: ' 0 24px 24px' }}>
          <Alert severity="success">{t('payment-success')}</Alert>
        </Box>
      ) : paymentPayerStatus?.status === EPaymentPayerStatus.DECLINED ||
        paymentPayerStatus?.status === EPaymentPayerStatus.ERROR ? (
        <Box sx={{ p: ' 0 24px 24px' }}>
          <Alert severity="error">{t('payment-error')}</Alert>
        </Box>
      ) : null}
    </Stack>
  )
}
